import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient, UseMutationOptions } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import learnBackendRequestHandler from './learnBackendRequestHandler'
import playBackendRequestHandler from './playBackendRequestHandler'
import { generatePath } from '../shared/UrlUtils/urlUtils'
import { queryKeys } from './queryKeys'
import { buildMergedTopics } from '../shared/CourseUtils/courseUtils'
import { TopicStatus } from '../enums'

export function useCourses(queryParams?: Record<string, string | string[]>) {
	return useQuery({
		queryKey: queryKeys.courses(queryParams),
		queryFn: async ({ signal }): Promise<AxiosResponse<CourseListItem[]>> =>
			learnBackendRequestHandler(
				'courses',
				undefined,
				undefined,
				undefined,
				queryParams,
				{ signal },
			),
		placeholderData: previousData => previousData,
	})
}

export function useCourseAutocomplete(search: string) {
	return useQuery({
		queryKey: queryKeys.courseAutocomplete(search),
		queryFn: async ({
			signal,
		}): Promise<AxiosResponse<CourseAutocompleteResponse>> =>
			learnBackendRequestHandler(
				'courseAutocomplete',
				undefined,
				undefined,
				undefined,
				{ search },
				{ signal },
			),
		enabled: search.length > 0,
	})
}

const selectCourseContent = (response: AxiosResponse<CourseDetail>) => ({
	course: response.data,
	mergedTopics: buildMergedTopics(response.data.topics),
})

export function useCourseContent(courseSlug: string) {
	const query = useQuery({
		queryKey: queryKeys.course(courseSlug),
		queryFn: async ({ signal }): Promise<AxiosResponse<CourseDetail>> =>
			learnBackendRequestHandler(
				'course',
				undefined,
				undefined,
				courseSlug,
				undefined,
				{ signal },
			),
		select: selectCourseContent,
		retry: false,
	})

	return {
		query,
		course: query.data?.course,
		mergedTopics: query.data?.mergedTopics,
	}
}

export function useTopic(courseSlug: string, topicSlug: string) {
	return useQuery({
		queryKey: queryKeys.topic(courseSlug, topicSlug),
		queryFn: async ({ signal }): Promise<AxiosResponse<Topic>> =>
			learnBackendRequestHandler(
				'topic',
				undefined,
				undefined,
				generatePath([courseSlug, 'topic', topicSlug]),
				undefined,
				{ signal },
			),
		retry: false,
	})
}

export function useTopicRepository(courseSlug: string, topicSlug: string) {
	return useQuery({
		queryKey: queryKeys.topicRepository(courseSlug, topicSlug),
		queryFn: async ({ signal }): Promise<AxiosResponse<{ url: string }>> =>
			learnBackendRequestHandler(
				'repository',
				undefined,
				undefined,
				generatePath([courseSlug!, 'topic', topicSlug!, 'repo']),
				undefined,
				{ signal },
			),
	})
}

export function useTags(queryParams?: Record<string, string | string[]>) {
	return useQuery({
		queryKey: queryKeys.tags(queryParams),
		queryFn: async ({ signal }): Promise<AxiosResponse<Tag[]>> =>
			learnBackendRequestHandler(
				'tags',
				undefined,
				undefined,
				undefined,
				queryParams,
				{ signal },
			),
	})
}

interface UpdateTopicStatusVariables {
	courseSlug: string
	topicSlug: string
	nextStatus: TopicStatus.Started | TopicStatus.Completed
}

interface UpdateTopicStatusContext {
	courseKey: ReturnType<typeof queryKeys.course>
	topicKey: ReturnType<typeof queryKeys.topic>
	previousTopicResponse: AxiosResponse<Topic> | undefined
	previousCourseResponse: AxiosResponse<CourseDetail> | undefined
	completionStatus: CompletionStatus
}

export function buildUpdateTopicStatusMutationOptions(
	queryClient: QueryClient,
): UseMutationOptions<
	AxiosResponse,
	Error,
	UpdateTopicStatusVariables,
	UpdateTopicStatusContext
> {
	return {
		mutationFn: async ({ courseSlug, topicSlug, nextStatus }) => {
			const response = await learnBackendRequestHandler(
				'updateTopicStatus',
				{ status: nextStatus },
				undefined,
				generatePath([courseSlug, 'topic', topicSlug, 'status']),
			)
			if (response.status !== 200 && response.status !== 204) {
				throw new Error(
					`Unexpected response status ${response.status} while updating topic status`,
				)
			}
			return response
		},
		onMutate: async ({ courseSlug, topicSlug, nextStatus }) => {
			const courseKey = queryKeys.course(courseSlug)
			const topicKey = queryKeys.topic(courseSlug, topicSlug)
			await Promise.all([
				queryClient.cancelQueries({ queryKey: courseKey, exact: true }),
				queryClient.cancelQueries({ queryKey: topicKey, exact: true }),
			])

			const previousTopicResponse =
				queryClient.getQueryData<AxiosResponse<Topic>>(topicKey)
			const previousCourseResponse =
				queryClient.getQueryData<AxiosResponse<CourseDetail>>(courseKey)

			const now = new Date().toISOString()
			const completionStatus: CompletionStatus = {
				status: nextStatus,
				started_at:
					previousTopicResponse?.data.completion_status?.started_at ??
					previousCourseResponse?.data.topics.find(
						(topic: Topic) => topic.slug === topicSlug,
					)?.completion_status?.started_at ??
					now,
				completed_at: nextStatus === TopicStatus.Completed ? now : null,
			}

			if (previousTopicResponse) {
				queryClient.setQueryData<AxiosResponse<Topic>>(topicKey, {
					...previousTopicResponse,
					data: {
						...previousTopicResponse.data,
						completion_status: completionStatus,
					},
				})
			}
			if (previousCourseResponse) {
				queryClient.setQueryData<AxiosResponse<CourseDetail>>(courseKey, {
					...previousCourseResponse,
					data: {
						...previousCourseResponse.data,
						topics: previousCourseResponse.data.topics.map((topic: Topic) =>
							topic.slug === topicSlug
								? { ...topic, completion_status: completionStatus }
								: topic,
						),
					},
				})
			}

			return {
				courseKey,
				topicKey,
				previousTopicResponse,
				previousCourseResponse,
				completionStatus,
			}
		},
		onError: (_error, _variables, context) => {
			if (context?.previousTopicResponse) {
				queryClient.setQueryData(context.topicKey, context.previousTopicResponse)
			}
			if (context?.previousCourseResponse) {
				queryClient.setQueryData(context.courseKey, context.previousCourseResponse)
			}
		},
		onSuccess: async (_data, _variables, context) => {
			if (!context) return
			await queryClient.cancelQueries({
				queryKey: context.topicKey,
				exact: true,
			})
			queryClient.setQueryData<AxiosResponse<Topic>>(
				context.topicKey,
				cachedResponse =>
					cachedResponse
						? {
								...cachedResponse,
								data: {
									...cachedResponse.data,
									completion_status: context.completionStatus,
								},
							}
						: cachedResponse,
			)
		},
		onSettled: (_data, _error, { courseSlug, topicSlug, nextStatus }) => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.course(courseSlug),
				exact: true,
			})
			queryClient.invalidateQueries({
				queryKey: queryKeys.topic(courseSlug, topicSlug),
				exact: true,
			})
			if (nextStatus === TopicStatus.Completed) {
				queryClient.invalidateQueries({ queryKey: ['learning-path'] })
				queryClient.invalidateQueries({ queryKey: ['user-learning-path'] })
			}
		},
	}
}

export function useUpdateTopicStatus() {
	const queryClient = useQueryClient()

	return useMutation(buildUpdateTopicStatusMutationOptions(queryClient))
}

export function useUserCustomisation(enabled = true) {
	return useQuery({
		queryKey: ['user-customisation'],
		queryFn: async (): Promise<AxiosResponse<BackendUserCustomisation>> =>
			learnBackendRequestHandler('userCustomisationGet'),
		enabled,
		gcTime: 300_000,
		staleTime: 120_000,
		retry: false,
	})
}

export function useSaveUserCustomisation() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async ({
			payload,
			isUpdate,
		}: {
			payload: UserCustomisationPayload
			isUpdate: boolean
		}): Promise<AxiosResponse<BackendUserCustomisation>> =>
			learnBackendRequestHandler(
				isUpdate ? 'userCustomisationUpdate' : 'userCustomisationCreate',
				payload,
			),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['user-customisation'] })
		},
	})
}

export function useLearningPath(enabled = true) {
	return useQuery({
		queryKey: ['learning-path'],
		queryFn: async (): Promise<AxiosResponse<BackendLearnPathNode[]>> => {
			const response: AxiosResponse<BackendLearnPathNode[]> =
				await learnBackendRequestHandler('learningPathGet')

			response.data = await Promise.all(
				response.data.map(async node => {
					try {
						const content: AxiosResponse<LearningPathContentResponse> =
							node.type === 'Challenge'
								? await playBackendRequestHandler(
										'challenge',
										undefined,
										undefined,
										node.type_uuid,
									)
								: await learnBackendRequestHandler(
										node.type === 'Topic' ? 'learningPathTopic' : 'course',
										undefined,
										undefined,
										node.type_uuid,
									)
						return {
							...node,
							title: content.data.title,
							slug: content.data.slug,
							course_slug: content.data.course_slug,
						}
					} catch {
						// A deleted or unavailable resource must not prevent the rest of
						// the pathway from rendering.
						return node
					}
				}),
			)

			return response
		},
		enabled,
		gcTime: 300_000,
		staleTime: 60_000,
		retry: false,
	})
}

export function useUserLearningPath(enabled = true) {
	return useQuery({
		queryKey: ['user-learning-path'],
		queryFn: async (): Promise<AxiosResponse<BackendUserLearningPath>> =>
			learnBackendRequestHandler('userLearningPathGet'),
		enabled,
		gcTime: 300_000,
		staleTime: 60_000,
		retry: false,
	})
}

export function useRequestLearningPath() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async ({
			clear = false,
			uuid,
		}: { clear?: boolean; uuid?: string[] } = {}): Promise<
			AxiosResponse<{ detail: string }>
		> =>
			learnBackendRequestHandler(
				'learningPathCreate',
				undefined,
				undefined,
				undefined,
				uuid && uuid.length > 0
					? { uuid }
					: clear
						? { clear: 'true' }
						: undefined,
			),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['learning-path'] })
		},
	})
}

/**
 * Resolve a Play challenge URL by iterating games until the challenge is found.
 *
 * 1. Fetches the challenge by UUID to get its slug.
 * 2. Fetches all visible games, then their challenges one game at a time,
 *    stopping as soon as a matching slug is found.
 * 3. Returns the frontend URL:
 *    <VITE_SECDIM_PLAY_FRONTEND>game/<game_slug>/challenge/<challenge_slug>
 */
export function useChallengeUrl(
	challengeUuid: string | undefined,
	enabled: boolean,
) {
	return useQuery({
		queryKey: ['challenge-url', challengeUuid],
		queryFn: async (): Promise<string | undefined> => {
			if (!challengeUuid) return undefined

			// Get the challenge slug from the detail endpoint.
			const challengeResp = await playBackendRequestHandler(
				'challenge',
				undefined,
				undefined,
				challengeUuid,
			)
			const challengeSlug: string | undefined = challengeResp.data?.slug
			if (!challengeSlug) return undefined

			// Fetch all games, then for each game fetch its challenges.
			const gamesResp = await playBackendRequestHandler('games')
			const games: Array<{ slug: string }> = gamesResp.data ?? []

			for (const game of games) {
				const challengesResp = await playBackendRequestHandler(
					'gameChallenges',
					undefined,
					undefined,
					`${game.slug}/challenges`,
				)
				const challenges: Array<{ slug: string }> = challengesResp.data ?? []
				const found = challenges.find(c => c.slug === challengeSlug)
				if (found) {
					return `${import.meta.env.VITE_SECDIM_PLAY_FRONTEND}game/${game.slug}/challenge/${challengeSlug}`
				}
			}

			// Challenge exists but isn't in any visible game — link to Play root.
			return import.meta.env.VITE_SECDIM_PLAY_FRONTEND!
		},
		enabled,
		staleTime: 600_000,
		retry: false,
	})
}
